import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, FileCheck, AlertTriangle } from 'lucide-react';

/**
 * SBA Loan Eligibility (Above $350K, up to $5M) — single source of truth.
 *
 * Per Quidity SBA Above-$350K Underwriting Matrix:
 *   Max Loan = MIN(
 *     Amount supported by Business DSCR at 1.15x,
 *     Amount supported by Global DSCR at 1.00x,
 *     35% of Annual Revenue
 *   )
 * Minimum Time in Business: 3 years (4 years for construction).
 * Minimum Annual Revenue: $100,000.
 *
 * Sizing uses SBA 7(a) standard:
 *   Rate = Prime + 2.75% (default Prime 8.50% → 11.25%; user-overrideable later).
 *   Term = 10 years (120 months) standard 7(a) working-capital amortization.
 *
 * Global DSCR requires personal income + personal debt service, which usually
 * aren't extracted from the business tax return. If `personalIncome` is not
 * provided we conservatively assume personal income = 0 (worst case) so the
 * global cap is the SAME as the business cap minus personal debt; if
 * `personalDebtService` is also missing we mark global DSCR "Pending PFS"
 * and only use Business DSCR + Revenue caps in the final max.
 */

const SBA_FLOOR = 350000;
const SBA_CEILING = 5000000;
const BUSINESS_DSCR_MIN = 1.15;
const GLOBAL_DSCR_MIN = 1.0;
const REVENUE_FACTOR = 0.35;
const SBA_RATE = 0.1125; // 11.25% (Prime 8.50% + 2.75%)
const SBA_TERM_MONTHS = 120;
const MIN_TIB_YEARS = 3;
const MIN_TIB_YEARS_CONSTRUCTION = 4;
const MIN_REVENUE = 100000;

const REQUIRED_DOCS = [
  '3 years business tax returns',
  '12 months business bank statements',
  'Business debt schedule',
  'Affiliate debt schedules',
  'Personal debt schedules',
  'Interim Profit & Loss Statement (within 90 days)',
  'Interim Balance Sheet (within 90 days)',
];

const fmt$ = (v) => `$${Math.round(parseFloat(v) || 0).toLocaleString('en-US')}`;

const calcMonthlyPayment = (principal, annualRate, termMonths) => {
  if (principal <= 0 || termMonths <= 0) return 0;
  const r = annualRate / 12;
  return principal * (r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1);
};

const maxLoanFromDSCR = (availableAnnualDebtService, annualRate, termMonths) => {
  if (availableAnnualDebtService <= 0) return 0;
  const r = annualRate / 12;
  const pvFactor = (1 - Math.pow(1 + r, -termMonths)) / r;
  return (availableAnnualDebtService / 12) * pvFactor;
};

/**
 * @param {{
 *   ebitda: number,            // business EBITDA
 *   revenue: number,           // annual gross revenue
 *   existingBusinessDebt: number,
 *   personalIncome?: number,        // optional, from PFS
 *   personalDebtService?: number,   // optional, from PFS
 *   timeInBusinessYears?: number,   // optional, from app
 *   industry?: string,              // optional, used to flag construction
 * }} props
 */
const SBAEligibilityCard = ({
  ebitda,
  revenue,
  existingBusinessDebt = 0,
  personalIncome,
  personalDebtService,
  timeInBusinessYears,
  industry,
  compact = false,
}) => {
  const [docsOpen, setDocsOpen] = useState(false);

  const safeEbitda = parseFloat(ebitda) || 0;
  const safeRevenue = parseFloat(revenue) || 0;
  const safeExistingDebt = parseFloat(existingBusinessDebt) || 0;

  // Business DSCR cap
  const businessMaxADS = safeEbitda / BUSINESS_DSCR_MIN;
  const businessAvailableADS = businessMaxADS - safeExistingDebt;
  const businessCap = maxLoanFromDSCR(businessAvailableADS, SBA_RATE, SBA_TERM_MONTHS);

  // Global DSCR cap (only computable when personalIncome+personalDebtService provided)
  const hasGlobalData = personalIncome != null && personalDebtService != null;
  let globalCap = null;
  if (hasGlobalData) {
    const globalEbitda = safeEbitda + (parseFloat(personalIncome) || 0);
    const globalMaxADS = globalEbitda / GLOBAL_DSCR_MIN;
    const globalAvailableADS = globalMaxADS - safeExistingDebt - (parseFloat(personalDebtService) || 0);
    globalCap = maxLoanFromDSCR(globalAvailableADS, SBA_RATE, SBA_TERM_MONTHS);
  }

  // Revenue cap
  const revenueCap = safeRevenue * REVENUE_FACTOR;

  // Final max — apply caps that we actually have, then floor at $350K and ceiling at $5M
  const candidates = [businessCap, revenueCap];
  if (hasGlobalData) candidates.push(globalCap);
  const rawMax = Math.max(0, Math.min(...candidates));
  const finalMax = Math.min(SBA_CEILING, rawMax);
  const meetsFloor = finalMax >= SBA_FLOOR;

  // Qualification checks
  const isConstruction = industry && /construct/i.test(String(industry));
  const requiredTIB = isConstruction ? MIN_TIB_YEARS_CONSTRUCTION : MIN_TIB_YEARS;
  const meetsRevenue = safeRevenue >= MIN_REVENUE;
  const tibKnown = timeInBusinessYears != null && !Number.isNaN(parseFloat(timeInBusinessYears));
  const meetsTIB = tibKnown ? (parseFloat(timeInBusinessYears) >= requiredTIB) : null;
  const meetsEbitda = safeEbitda > 0;

  const qualifies = meetsFloor && meetsRevenue && meetsEbitda && (meetsTIB !== false);

  // Projected DSCR with max loan
  const monthlyPmtAtMax = calcMonthlyPayment(finalMax, SBA_RATE, SBA_TERM_MONTHS);
  const projectedBusinessDSCR = (safeExistingDebt + monthlyPmtAtMax * 12) > 0
    ? safeEbitda / (safeExistingDebt + monthlyPmtAtMax * 12)
    : null;

  const bindingConstraint = (() => {
    if (finalMax === SBA_CEILING) return 'Program cap ($5M)';
    const ordered = [
      { name: '35% of Revenue', value: revenueCap },
      { name: 'Business DSCR (1.15x)', value: businessCap },
    ];
    if (hasGlobalData) ordered.push({ name: 'Global DSCR (1.00x)', value: globalCap });
    ordered.sort((a, b) => a.value - b.value);
    return ordered[0]?.name || '—';
  })();

  return (
    <div
      className={`border rounded-lg ${qualifies ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'} ${compact ? 'p-3' : 'p-4'}`}
      data-testid="sba-jumbo-eligibility-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className={`text-xs font-bold uppercase text-slate-700 ${compact ? '' : 'tracking-wider'}`}>SBA Loan — Above $350K (up to $5M)</p>
          <p className="text-[10px] text-slate-500 mt-0.5">11.25% sizing rate · 10-yr amortization</p>
        </div>
        <Badge className={`text-[10px] ${qualifies ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
          {qualifies ? 'Potentially Eligible' : 'Does Not Qualify'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <div className="bg-white border rounded p-2.5">
          <p className="text-[10px] uppercase text-slate-500 mb-0.5">Max Loan Amount</p>
          <p className="text-lg font-black text-slate-800" data-testid="sba-jumbo-max">{fmt$(finalMax)}</p>
          {finalMax > 0 && (
            <p className="text-[10px] text-slate-500 mt-0.5">Binding: <span className="font-semibold text-slate-700">{bindingConstraint}</span></p>
          )}
        </div>
        <div className="bg-white border rounded p-2.5">
          <p className="text-[10px] uppercase text-slate-500 mb-0.5">Projected Business DSCR</p>
          <p
            className={`text-lg font-black ${
              projectedBusinessDSCR == null
                ? 'text-slate-400'
                : projectedBusinessDSCR >= BUSINESS_DSCR_MIN
                  ? 'text-emerald-700'
                  : 'text-red-600'
            }`}
            data-testid="sba-jumbo-projected-dscr"
          >
            {projectedBusinessDSCR != null ? `${projectedBusinessDSCR.toFixed(2)}x` : 'N/A'}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">Monthly P&I: {fmt$(monthlyPmtAtMax)}</p>
        </div>
      </div>

      {/* Sizing breakdown */}
      <div className="bg-white/70 border border-slate-200 rounded p-2.5 text-xs space-y-1 mb-3">
        <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">Sizing Caps</p>
        <div className="flex justify-between"><span className="text-slate-600">Business DSCR @ 1.15x</span><span className="font-semibold">{fmt$(Math.max(0, businessCap))}</span></div>
        <div className="flex justify-between">
          <span className="text-slate-600">Global DSCR @ 1.00x</span>
          <span className="font-semibold">
            {hasGlobalData ? fmt$(Math.max(0, globalCap)) : <span className="text-amber-600 italic">Pending PFS</span>}
          </span>
        </div>
        <div className="flex justify-between"><span className="text-slate-600">35% of Annual Revenue</span><span className="font-semibold">{fmt$(Math.max(0, revenueCap))}</span></div>
      </div>

      {/* Qualification checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-[11px] mb-3">
        <div className={`flex items-center gap-1.5 ${meetsRevenue ? 'text-emerald-700' : 'text-red-600'}`}>
          <span className="font-bold">{meetsRevenue ? '✓' : '✗'}</span>
          <span>Min Revenue $100K ({fmt$(safeRevenue)})</span>
        </div>
        <div className={`flex items-center gap-1.5 ${meetsEbitda ? 'text-emerald-700' : 'text-red-600'}`}>
          <span className="font-bold">{meetsEbitda ? '✓' : '✗'}</span>
          <span>Positive EBITDA ({fmt$(safeEbitda)})</span>
        </div>
        <div className={`flex items-center gap-1.5 ${
          meetsTIB === null ? 'text-amber-600' : meetsTIB ? 'text-emerald-700' : 'text-red-600'
        }`}>
          <span className="font-bold">{meetsTIB === null ? '?' : meetsTIB ? '✓' : '✗'}</span>
          <span>
            Min {requiredTIB}yr TIB{isConstruction ? ' (construction)' : ''}
            {tibKnown ? ` (${parseFloat(timeInBusinessYears).toFixed(1)}y)` : ' — verify'}
          </span>
        </div>
      </div>

      {!hasGlobalData && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3" data-testid="sba-jumbo-global-warning">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>Global DSCR not computed — requires personal income + personal debt from PFS. Final eligible amount may be lower.</span>
        </div>
      )}

      {/* Required docs (collapsible) */}
      <button
        type="button"
        onClick={() => setDocsOpen(!docsOpen)}
        className="w-full flex items-center justify-between text-[11px] font-semibold text-slate-600 hover:text-slate-800 py-1.5 px-2 rounded hover:bg-slate-100 transition-colors"
        data-testid="sba-jumbo-docs-toggle"
      >
        <span className="flex items-center gap-1.5">
          <FileCheck className="h-3.5 w-3.5" />
          Required Documents ({REQUIRED_DOCS.length})
        </span>
        {docsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {docsOpen && (
        <ul className="mt-1.5 ml-5 space-y-0.5 text-[11px] text-slate-600 list-disc" data-testid="sba-jumbo-docs-list">
          {REQUIRED_DOCS.map(d => <li key={d}>{d}</li>)}
        </ul>
      )}
    </div>
  );
};

export default SBAEligibilityCard;
